import { useSelector } from "react-redux";
import { topOffenderFilterSelector } from "../../../store/filters/topoffender/selector";
import { useGetAlarmCountQuery, useGetCellsCountsQuery, useTopOffendersQuery } from "../../../store/api/devices/deviceApiSlice";
import { API } from "../../../globals/globals";
import { useEffect } from "react";
import { selectedGeoRegionsSelector } from "../../../store/api/admin/selectors";
import { IGeoRegion } from "../../../store/api/admin/types";

export const useAlarmDataGrid = () => {

  const paperSx = {
    backgroundColor: "var(--card)",
    borderRadius: "calc(var(--radius) + 4px)",
    border: "1px solid var(--border)",
    padding: 2,
    height: "100%",
    overflow: "hidden",
  };

  const titleSx = {
    mx: 6,
    mt: 6,
    mb: 2,
    color: "var(--card-foreground)",
    fontWeight: "var(--font-weight-semibold, 600)",
  };

  const gridContainerSx = {
    width: "100%",
    height: 600,
  };

  return {
    paperSx,
    titleSx,
    gridContainerSx,
  }
}

export const useCellsCardCount = (selectedGeoRegions: string[] = []) => {
  const queryArg = selectedGeoRegions.length > 0 ? selectedGeoRegions : undefined;

  const { data, isLoading, error, refetch } = useGetCellsCountsQuery(queryArg, {
    pollingInterval: API.ENDPOINTS.CELLSCOUNTS.pollingInterval || API.POLLING_INTERVAL,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetch();
  }, [selectedGeoRegions.join(','), refetch]);

  return {
    data,
    isLoading,
    error,
  };
}

export const useAlarmCardCount = (selectedGeoRegions: string[] = []) => {
  const queryArg = selectedGeoRegions.length > 0 ? selectedGeoRegions : undefined;

  const { data, isLoading, error, refetch } = useGetAlarmCountQuery(queryArg, {
    pollingInterval: API.ENDPOINTS.ALARMCOUNT.pollingInterval || API.POLLING_INTERVAL,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetch();
  }, [selectedGeoRegions.join(','), refetch]);

  return {
    data,
    isLoading,
    error,
  };
}

export const useTopOffenderDisplay = (
  page: number,
  size: number,
  search?: string
) => {
  const filterState = useSelector(topOffenderFilterSelector);
  const selectedGeoRegions = useSelector(selectedGeoRegionsSelector) as IGeoRegion[];
  const selectedGeoRegionNames = selectedGeoRegions?.map((r: IGeoRegion) => r.name) || [];

  const params = new URLSearchParams();

  params.append("page", String(page));
  params.append("size", String(size));


  // Only pass geoRegion if a specific region is selected, else pass all as comma-separated geoRegion
  if (filterState.selectedRegion) {
    params.append("geoRegions", filterState.selectedRegion);
  } else if (selectedGeoRegionNames.length > 0) {
    params.append("geoRegions", selectedGeoRegionNames.join(","));
  }

  if (filterState.selectedType)
    params.append("type", filterState.selectedType);

  if (filterState.selectedStatus)
    params.append("status", filterState.selectedStatus);

  if (filterState.selectedConfigStatus)
    params.append("configStatus", filterState.selectedConfigStatus);

  if (search && search.trim())
    params.append("search", search.trim());

  if (filterState.selectedAlarmType?.length) {
    filterState.selectedAlarmType.forEach((alarm: string) => {
      params.append("alarmType", alarm);
    });
  }

  const { data, isLoading } = useTopOffendersQuery(params.toString());

  const response = data as any;

  return {
    rows: response?.data || [],
    totalElements: response?.totalItems || 0,
    isLoading,
  };
};
